import React, {useLayoutEffect} from 'react'

const ARROW_VAR = '--settings-linked-arrow-left'

const readRefElement = <T extends HTMLElement>(
  ref: React.RefObject<T | null> | React.Ref<T>
): T | null => {
  if (typeof ref === 'function') {
    return null
  }
  if (ref && typeof ref === 'object' && 'current' in ref) {
    return ref.current
  }
  return null
}

/**
 * Positions a child-panel speech-bubble arrow under the parent control
 * (switch/checkbox) by setting --settings-linked-arrow-left on the panel.
 */
export const useLinkedSettingsArrowOffset = (
  enabled: boolean,
  anchorRef: React.RefObject<HTMLElement | null> | React.Ref<HTMLElement>,
  panelRef: React.RefObject<HTMLElement | null> | React.Ref<HTMLElement>
) => {
  useLayoutEffect(() => {
    if (!enabled) return

    const syncArrow = () => {
      const anchor = readRefElement(anchorRef)
      const panel = readRefElement(panelRef)
      if (!anchor || !panel) return

      const control =
        anchor.querySelector<HTMLElement>('.form-check-input') ??
        anchor.querySelector<HTMLElement>('input[type="checkbox"]') ??
        anchor.querySelector<HTMLElement>('input[type="radio"]') ??
        anchor

      const controlRect = control.getBoundingClientRect()
      const panelRect = panel.getBoundingClientRect()
      if (panelRect.width <= 0) return

      const left = controlRect.left + controlRect.width / 2 - panelRect.left
      const clamped = Math.min(Math.max(left, 16), panelRect.width - 16)
      panel.style.setProperty(ARROW_VAR, `${clamped}px`)
    }

    syncArrow()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncArrow) : null
    const panelEl = readRefElement(panelRef)
    const anchorEl = readRefElement(anchorRef)
    if (panelEl) resizeObserver?.observe(panelEl)
    if (anchorEl) resizeObserver?.observe(anchorEl)

    window.addEventListener('resize', syncArrow)
    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', syncArrow)
    }
  }, [enabled, anchorRef, panelRef])
}
